import { checkEmailAddress, checkIfTeamExists } from '@/controllers';
import { isAtLeastTenYearsAgo } from './dateTools';

export const urlRegex = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,6})(\/\S*)?$/i;
export const instagramRegex = /^(?!.*[._]{2})(?![._])([a-zA-Z0-9._]{1,30})(?<![._])$/i;
export const vimeoRegex = /^(?!.*[-_]{2})(?![-_])[a-zA-Z0-9-_]{3,30}(?<![-_])$/i;
export const youtubeChannelRegex = /^(?!.*[._]{2})(?![._])[a-zA-Z0-9._]{3,30}(?<![._])$/i;
export const twitterRegex = /^@[a-zA-Z0-9_]{4,15}$/i;
export const facebookRegex = /^(?!.*\.\.)(?!\.)([a-zA-Z0-9.]{5,50})(?<!\.)$/i;
export const phoneRegex = /^(\+?\d{1,3})?[-.\s]?(\(?\d{3}\)?)?[-.\s]?\d{3}[-.\s]?\d{4}$/i;
export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export const postalCodeRegex = /^[A-Za-z0-9- ]{3,10}$/;
export const passwordRegex =
  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}\-|<>\[\]\~\`\_\=\+\\\;\'\/])[A-Za-z\d!@#$%^&*(),.?":{}\-|<>\[\]\~\`\_\=\+\\\;\'\/]{8,}$/;
export const rules = {
  required: (value: string) => (!!value && value != '') || 'Required.',
  passwordLength: (value: string) => value.length >= 8 || 'Password must be at least 8 characters.',
  passwordComplexity: (value: string) =>
    passwordRegex.test(value) ||
    'Password must be at least 8 characters long with a number and special character',
  passwordMatch: (p1: string, p2: string) => {
    return p1 === p2 || 'Passwords do not match';
  },
  postalCode: (value: string) => postalCodeRegex.test(value) || 'Postal Code is not valid',
  phone: (value: string) => phoneRegex.test(value) || 'Phone Number is not valid',
  email: (value: string) => emailRegex.test(value) || 'Email is not valid',
  emailEDU: (value: string) => value.endsWith('.edu') || 'Email Address must be a .edu address',
  numeric: (value: string) => !value || !isNaN(parseFloat(value)) || 'Must be a number',
  postiveNumber: (value: number) => value >= 0 || 'Must be a positive number',
  url: (value: string) => !value || urlRegex.test(value) || 'Url is not valid',
  instagram: (value: string) =>
    !value || instagramRegex.test(value) || 'Instagram username is not valid',
  vimeo: (value: string) => !value || vimeoRegex.test(value) || 'Vimeo username is not valid',
  youtube: (value: string) =>
    !value || youtubeChannelRegex.test(value) || 'YouTube Channel is not valid',
  twitter: (value: string) => !value || twitterRegex.test(value) || 'X username is not valid',
  facebook: (value: string) =>
    !value || facebookRegex.test(value) || 'Facebook username is not valid',
  birthday: (value: Date) =>
    !value || isAtLeastTenYearsAgo(value) || 'Sorry, you are too young to be a team leader',
  emailUnique: async (value: string, userId?: string) => {
    const isUsed = await hasEmailBeenUsed(value, userId);
    return !value || !isUsed || 'This email address is already has an account.';
  },
  teamUnique: async (value: string, teamId?: string) => {
    const isUsed = await hasTeamBeenUsed(value, teamId);
    return !value || !isUsed || 'There is already a Team with that name.';
  },
};

export const hasEmailBeenUsed = async (
  email_address: string,
  userId?: string,
): Promise<boolean> => {
  let used = false;
  try {
    if (email_address != null && email_address.trim() !== '') {
      const check = await checkEmailAddress(email_address, userId);
      used = check;
    }
  } catch {}
  return used;
};

const hasTeamBeenUsed = async (name: string, teamId?: string): Promise<boolean> => {
  let used = false;

  try {
    if (name != null && name.trim() !== '') {
      const check = await checkIfTeamExists(name, teamId);
      used = check;
    } else {
      used = false; // Reset to default if input is empty
    }
  } catch {}
  return used;
};
